import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ACTION_PRIORITIES, dueDateFor, type ActionPriority } from '@bp/shared';
import { api } from '@/lib/api';
import type { ActionItem, ResponseRow, Worker } from '@/lib/types';
import { Button, Dialog, ErrorNote, Field, Input, Select, Textarea } from '../ui';

type Assignable = { id: string; name: string; role: string };

// Shared by "create task from a failed item" and "edit task".
export function AssigneeFields({ storeId, userId, workerId, onChange }: {
  storeId: string; userId: string | null; workerId: string | null; onChange: (v: { assignee_user_id: string | null; assignee_worker_id: string | null }) => void;
}) {
  const users = useQuery({ queryKey: ['assignable', storeId], queryFn: () => api.get<Assignable[]>(`/api/users/assignable?store_id=${storeId}`) });
  const workers = useQuery({ queryKey: ['workers', storeId], queryFn: () => api.get<Worker[]>(`/api/stores/${storeId}/workers`) });
  const value = workerId ? `w:${workerId}` : userId ? `u:${userId}` : '';
  return (
    <Field label="Assign to" hint="Store workers without a login show up on the store tablet's task list.">
      <Select
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ assignee_user_id: v.startsWith('u:') ? v.slice(2) : null, assignee_worker_id: v.startsWith('w:') ? v.slice(2) : null });
        }}
      >
        <option value="">Unassigned</option>
        {(workers.data?.length ?? 0) > 0 && (
          <optgroup label="Store workers">{workers.data!.map((w) => <option key={w.id} value={`w:${w.id}`}>{w.name}{w.position && ` — ${w.position}`}</option>)}</optgroup>
        )}
        {(users.data?.length ?? 0) > 0 && (
          <optgroup label="People with a login">{users.data!.map((u) => <option key={u.id} value={`u:${u.id}`}>{u.name}</option>)}</optgroup>
        )}
      </Select>
    </Field>
  );
}

export function CreateTaskDialog({ auditId, auditDate, storeId, response, open, onOpenChange, onCreated }: {
  auditId: string; auditDate: string; storeId: string; response: ResponseRow; open: boolean; onOpenChange: (o: boolean) => void; onCreated: (t: ActionItem) => void;
}) {
  const [title, setTitle] = useState(response.item_text);
  const [detail, setDetail] = useState(response.comment ?? '');
  const [priority, setPriority] = useState<ActionPriority>('medium');
  const [due, setDue] = useState(response.fix_by_window ? dueDateFor(response.fix_by_window, auditDate) ?? '' : '');
  const [assignee, setAssignee] = useState<{ assignee_user_id: string | null; assignee_worker_id: string | null }>({ assignee_user_id: null, assignee_worker_id: response.accountable_worker_id });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const t = await api.post<ActionItem>('/api/action-items', { audit_id: auditId, item_key: response.item_key, title, detail, priority, due_date: due || null, ...assignee });
      toast.success('Action item created.');
      onCreated(t);
      onOpenChange(false);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Create action item" footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} loading={busy}>Create</Button></>}>
      <div className="space-y-4">
        <Field label="What needs fixing"><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Details"><Textarea value={detail} onChange={(e) => setDetail(e.target.value)} /></Field>
        <AssigneeFields storeId={storeId} userId={assignee.assignee_user_id} workerId={assignee.assignee_worker_id} onChange={setAssignee} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Due date"><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
          <Field label="Priority">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as ActionPriority)}>
              {ACTION_PRIORITIES.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
            </Select>
          </Field>
        </div>
        <ErrorNote error={error} />
      </div>
    </Dialog>
  );
}
